import { v4 as uuidv4 } from 'uuid';
import { deserializeNode } from './serializeNode';
import type { StrategyNode } from '../types/BaseNode';
import { SingleNode } from '../types/SingleNode';
import { CompositeNode } from '../types/CompositeNode';
import { FormatterNode } from '../types/FormatterNode';
import { PnLNode } from '../types/PnLNode';
import { VarianceNode } from '../types/VarianceNode';
import { MeepMeepNode } from '../types/MeepMeepNode';
import { RegressionNode } from '../types/RegressionNode';
import { getDefaultOperatorStatic } from '../hooks/useOperators';
import { getDefaultCombineOnStatic } from '../hooks/useCombineOptions';

type InsertHandler = (node: StrategyNode) => void;

export function handleNodeDrop(
  e: React.DragEvent,
  onInsert: InsertHandler
): void {
  e.preventDefault();
  e.stopPropagation();

  // Case 1: Saved strategy from sidebar
  const saved = e.dataTransfer.getData('application/moneymaker-saved-strategy');
  const copied = e.dataTransfer.getData('application/moneymaker-subtree');
  if (saved || copied) {
    try {
      const incomingJson = saved || copied

      const parsed = JSON.parse(incomingJson);
      const deserialized = deserializeNode(parsed.data ?? parsed);
      const clone = deserialized.clone();


      onInsert(clone);
    } catch (err) {
      console.error('Failed to deserialize dropped strategy:', err);
    }
    return;
  }

  // Case 2: New component from side panel
  const type = e.dataTransfer.getData('application/reactflow') as StrategyNode['type'];
  let newNode: StrategyNode;

  if (type === 'Single') {
    newNode = new SingleNode(uuidv4());
    newNode.config = { typeId: '', props: {} };
  } else if (type === 'Composite') {
    newNode = new CompositeNode(uuidv4(), undefined, getDefaultOperatorStatic(), getDefaultCombineOnStatic());
    newNode.config = { operator: newNode.config.operator, combine_on: newNode.config.combine_on, left: undefined, right: undefined };
  } else if (type === 'Formatter') {
    newNode = new FormatterNode(uuidv4());
    newNode.config = { typeId: "", props: {}, child: null };
  } else if (type === 'PnL') {
    newNode = new PnLNode(uuidv4(), "PnL");
  } else if (type === 'Variance') {
    newNode = new VarianceNode(uuidv4(), "Variance");
  } else if (type === 'MeepMeep') {
    newNode = new MeepMeepNode(uuidv4(), "MeepMeep Strategy");
  } else if (type === 'Regression') {
    newNode = new RegressionNode(uuidv4(), "Regression Model");
  } else {
    console.warn('Unknown drag type dropped:', type);
    return;
  }

  onInsert(newNode);
}

export function cloneNode(node: StrategyNode): StrategyNode {
  if (node.type === 'Single') {
    const cloned = new SingleNode(crypto.randomUUID(), node.title);
    cloned.title = node.title;

    cloned.config = JSON.parse(JSON.stringify(node.config)); // shallow enough
    return cloned;
  }

  if (node.type === 'Composite') {
    const cloned = new CompositeNode(crypto.randomUUID(), node.title, node.config.operator, node.config.combine_on);
    cloned.title = node.title;
    cloned.config.left = node.config.left ? cloneNode(node.config.left) : undefined;
    cloned.config.right = node.config.right ? cloneNode(node.config.right) : undefined;
    return cloned;
  }

  if (node.type === 'Formatter') {
    const cloned = new FormatterNode(crypto.randomUUID(), node.title);
    cloned.title = node.title;
    cloned.config = {
      typeId: node.config.typeId,
      props: { ...node.config.props },
      child: node.config.child ? cloneNode(node.config.child) : null,
    };
    return cloned;
  }

  if (node.type === 'PnL') {
    const cloned = new PnLNode(
      crypto.randomUUID(),
      node.title,
      node.config.entry ? cloneNode(node.config.entry) : undefined,
      node.config.exit ? cloneNode(node.config.exit) : undefined,
      node.config.maxLots,
      node.config.lotSize,
      node.config.fees,
      node.config.mode,
      node.isManualRename,
      node.config.exitMode,
      node.config.holdDuration,
      node.config.holdType
    );
    return cloned;
  }

  if (node.type === 'Variance') {
    const cloned = new VarianceNode(
      crypto.randomUUID(),
      node.title,
      node.config.target ? cloneNode(node.config.target) : undefined,
      (node.config.parameters ?? []).map((p: StrategyNode) => cloneNode(p))
    );
    return cloned;
  }

  if (node.type === 'MeepMeep') {
    const cloned = new MeepMeepNode(
      crypto.randomUUID(),
      node.title,
      node.config.main_market ? cloneNode(node.config.main_market) : undefined,
      node.config.signal ? cloneNode(node.config.signal) : undefined,
            node.config.latency_ms,
      node.config.insample_days,
      node.config.target_forward_ticks,
      node.config.max_volume,
      node.config.trade_margin,
      node.config.trade_margin_regime,
      node.isManualRename
    );
    return cloned;
  }

  if (node.type === 'Regression') {
    const cloned = new RegressionNode(
      crypto.randomUUID(),
      node.title,
      node.config.model_type,
      [...node.config.weights],
      node.config.inputs.map((input: StrategyNode) => cloneNode(input)),
      node.isManualRename,
      node.config.last_calibrated_at,
      node.config.calibration_artifact_id,
      node.config.calibration_artifact_path,
      node.config.feature_tree_hash,
      node.config.feature_labels ? [...node.config.feature_labels] : undefined
    );
    return cloned;
  }

  throw new Error(`Unknown node type: ${node.type}`);
}
